import { InjectedConnector } from '@web3-react/injected-connector'

// Poodl Network configuration
const POODL_CHAIN_ID = '0x3b9b' // 15259 in hex
const POODL_NETWORK_CONFIG = {
  chainId: POODL_CHAIN_ID,
  chainName: 'Poodl',
  nativeCurrency: {
    name: 'Poodl',
    symbol: 'POODL',
    decimals: 18
  },
  rpcUrls: ['https://rpc.poodl.org'],
  blockExplorerUrls: ['https://explorer.poodl.org']
}

/**
 * Attempts to add Poodl network to MetaMask
 */
export async function addPoodlNetwork(): Promise<boolean> {
  const ethereum = (window as any).ethereum
  if (!ethereum) {
    console.log('MetaMask not installed')
    return false
  }

  try {
    await ethereum.request({
      method: 'wallet_addEthereumChain',
      params: [POODL_NETWORK_CONFIG]
    })
    console.log('Poodl network added successfully')
    return true
  } catch (error) {
    console.error('Error adding Poodl network:', error)
    
    // If chain already exists, try to switch to it
    if ((error as any).code === 4902) {
      try {
        await ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: POODL_CHAIN_ID }]
        })
        console.log('Switched to Poodl network')
        return true
      } catch (switchError) {
        console.error('Error switching to Poodl network:', switchError)
        return false
      }
    }
    return false
  }
}

/**
 * Checks if user is on the correct network, if not, prompts to switch/add
 */
export async function ensurePoodlNetwork(): Promise<boolean> {
  const ethereum = (window as any).ethereum
  if (!ethereum) {
    return false
  }

  try {
    const chainId = await ethereum.request({ method: 'eth_chainId' })
    
    if (chainId === POODL_CHAIN_ID) {
      console.log('Already on Poodl network')
      return true
    }

    // Try to switch to Poodl network
    try {
      await ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: POODL_CHAIN_ID }]
      })
      console.log('Switched to Poodl network')
      return true
    } catch (switchError) {
      // If network doesn't exist (error code 4902), add it
      if ((switchError as any).code === 4902) {
        return await addPoodlNetwork()
      }
      throw switchError
    }
  } catch (error) {
    console.error('Error ensuring Poodl network:', error)
    return false
  }
}

/**
 * Attempts to auto-connect wallet on page load
 */
export async function autoConnectWallet(connector: InjectedConnector): Promise<boolean> {
  const ethereum = (window as any).ethereum
  if (!ethereum) {
    console.log('No Web3 provider found')
    return false
  }

  try {
    // Check if previously connected
    const isAuthorized = await connector.isAuthorized()
    
    if (isAuthorized) {
      console.log('Wallet previously authorized, auto-connecting...')
      
      // Ensure on correct network
      await ensurePoodlNetwork()
      
      // Activate connector
      await connector.activate()
      console.log('Wallet connected successfully')
      return true
    } else {
      console.log('Wallet not previously authorized')
      return false
    }
  } catch (error) {
    console.error('Error in auto-connect:', error)
    return false
  }
}

/**
 * Manually triggers wallet connection and network setup
 */
export async function connectWalletAndSetupNetwork(activate: (connector: InjectedConnector) => Promise<void>): Promise<boolean> {
  const ethereum = (window as any).ethereum
  if (!ethereum) {
    alert('Please install MetaMask or another Web3 wallet')
    return false
  }

  try {
    // First, ensure network is added/switched
    const networkReady = await ensurePoodlNetwork()
    
    if (!networkReady) {
      alert('Please add Poodl network to your wallet')
      return false
    }

    // Create connector
    const connector = new InjectedConnector({
      supportedChainIds: [15259] // Poodl chain ID
    })

    // Activate wallet connection
    await activate(connector)
    console.log('Wallet connected and network configured')
    return true
  } catch (error) {
    console.error('Error connecting wallet:', error)
    
    if ((error as any).code === 4001) {
      // User rejected
      console.log('User rejected connection')
    } else {
      alert('Failed to connect wallet. Please try again.')
    }
    return false
  }
}

/**
 * Gets current chain ID
 */
export async function getCurrentChainId(): Promise<string | null> {
  const ethereum = (window as any).ethereum
  if (!ethereum) {
    return null
  }

  try {
    const chainId = await ethereum.request({ method: 'eth_chainId' })
    return chainId
  } catch (error) {
    console.error('Error getting chain ID:', error)
    return null
  }
}

/**
 * Checks if on Poodl network
 */
export async function isOnPoodlNetwork(): Promise<boolean> {
  const chainId = await getCurrentChainId()
  return chainId === POODL_CHAIN_ID
}
